/**
 * Feature E02-F03-T07 — unsaved-changes tracking (Save / Discard / Cancel) +
 * localStorage draft persistence. Pure, framework-free, side-effect-free core so
 * the rules are unit-testable without a DOM/React.
 *
 * 1:1 port of `../EER app/public/assets/js/project.js` (resetUnsavedUpdates /
 * hasUnsavedChanges / collectPendingUpdates / saveLocalDraft / loadDraft /
 * clearDraft) and `dashboard.js` (navigate → showUnsavedDialog Save/Discard/Cancel,
 * STORAGE_KEY_DRAFT_PREFIX = 'eer.draft.' from core.js:33).
 *
 * The legacy app read its signals straight off the DOM (input values, whether an
 * inline edit block is `hidden`, the textarea, state.pendingFiles). In the v2
 * rewrite the React component owns those as state and passes a plain snapshot in,
 * so this module is the exact same decision logic over a typed snapshot. The
 * thresholds and field set are unchanged:
 *
 *   unsaved ⇔ any of:
 *     - unsavedUpdates.{client_name|role_name|contact|manual_context} present, OR
 *     - an inline edit block is OPEN (client/role/contact), OR
 *     - the manual-context textarea text differs from the saved project text, OR
 *     - there are pending (not-yet-uploaded) files.
 */
import type { ProjectUpdates } from '@eer/shared';

/** localStorage key prefix for a per-project draft (legacy core.js:33). */
export const STORAGE_KEY_DRAFT_PREFIX = 'eer.draft.';

/** The contact sub-object the inline contact edit collects. */
export type UnsavedContact = {
  readonly first_name?: string;
  readonly last_name?: string;
  readonly email?: string;
};

/**
 * The mutable bag of pending edits (legacy `state.unsavedUpdates`). Mirrors the
 * legacy null-initialised shape; `client_id` is tracked separately (it can be
 * `null` to mean "force new client", `undefined` to mean "untouched").
 */
export interface UnsavedUpdates {
  client_name: string | null;
  role_name: string | null;
  contact: UnsavedContact | null;
  manual_context: { text: string } | null;
  /** `undefined` = untouched, `null` = create-new, number = linked client id. */
  client_id?: number | null;
}

/** A minimal pending-file descriptor (parity with `state.pendingFiles`). */
export interface PendingFile {
  readonly name: string;
  /** The actual File in the browser; optional so the model stays DOM-free in tests. */
  readonly file?: File;
}

/**
 * A snapshot of everything that decides "are there unsaved changes". In the
 * legacy app these came off the DOM; here the React component supplies them.
 */
export interface UnsavedSnapshot {
  readonly unsavedUpdates: UnsavedUpdates;
  /** Whether the inline client edit block is currently open. */
  readonly clientEditOpen?: boolean;
  /** Whether the inline role edit block is currently open. */
  readonly roleEditOpen?: boolean;
  /** Whether the inline contact edit block is currently open. */
  readonly contactEditOpen?: boolean;
  /** Current text in the manual-context textarea. */
  readonly manualText?: string;
  /** The saved manual-context text on the project (`project.data.manual_context.text`). */
  readonly savedManualText?: string;
  /** Files queued for upload but not yet saved. */
  readonly pendingFiles?: readonly PendingFile[];
  /** Whether a project is currently being viewed (legacy `state.currentProject`). */
  readonly hasCurrentProject?: boolean;
}

/** Fresh, empty unsaved bag (legacy `resetUnsavedUpdates`). */
export function resetUnsavedUpdates(): UnsavedUpdates {
  return {
    client_name: null,
    role_name: null,
    contact: null,
    manual_context: null,
  };
}

/**
 * True when there is anything unsaved (legacy `hasUnsavedChanges`). Only relevant
 * while a project is open — with no current project there is nothing to lose.
 */
export function hasUnsavedChanges(s: UnsavedSnapshot): boolean {
  if (s.hasCurrentProject === false) return false;

  const manualText = (s.manualText ?? '').trim();
  const savedManualText = (s.savedManualText ?? '').trim();
  const manualTextChanged = manualText !== savedManualText;
  const pendingFilesCount = s.pendingFiles ? s.pendingFiles.length : 0;

  return Boolean(
    s.unsavedUpdates.client_name ||
    s.unsavedUpdates.role_name ||
    s.unsavedUpdates.contact ||
    s.unsavedUpdates.manual_context ||
    s.clientEditOpen ||
    s.roleEditOpen ||
    s.contactEditOpen ||
    manualTextChanged ||
    pendingFilesCount,
  );
}

/**
 * Collect the pending edits into the `updates` payload sent to PATCH /projects/:id
 * (legacy `collectPendingUpdates` + the `saveProjectChanges` merge of the open
 * inline edits, the textarea text, and `client_id`). Open inline-edit values win
 * over the stored bag (the user is mid-edit).
 */
export function collectPendingUpdates(s: UnsavedSnapshot): ProjectUpdates {
  const u = s.unsavedUpdates;
  const updates: ProjectUpdates = {};

  if (u.client_name) updates.client_name = u.client_name;
  if (u.role_name) updates.role_name = u.role_name;
  if (u.contact) updates.contact = u.contact;
  if (u.manual_context) updates.manual_context = u.manual_context;

  // Open inline edits override the stored bag (legacy reads the live inputs).
  if (s.clientEditOpen && u.client_name) updates.client_name = u.client_name;
  if (s.roleEditOpen && u.role_name) updates.role_name = u.role_name;
  if (s.contactEditOpen && u.contact) updates.contact = u.contact;

  // The textarea text is authoritative for manual_context.text when present.
  const manualText = (s.manualText ?? '').trim();
  if (manualText) {
    updates.manual_context = { ...(updates.manual_context ?? {}), text: manualText };
  }

  // client_id: undefined = untouched (omit), null = force-new, number = linked.
  if (u.client_id !== undefined) {
    updates.client_id = u.client_id ?? undefined;
    if (u.client_id === null) {
      // null means "create a new client from client_name" — drop the id, keep name.
      delete (updates as { client_id?: number }).client_id;
    }
  }

  return updates;
}

/** localStorage key for a project's draft. */
export function draftKey(projectId: number | string): string {
  return STORAGE_KEY_DRAFT_PREFIX + projectId;
}

/**
 * Persist the current unsaved bag to localStorage (legacy `saveLocalDraft`). The
 * textarea text is folded into `manual_context` first, matching the legacy node.
 */
export function saveLocalDraft(
  storage: Storage,
  projectId: number | string,
  s: UnsavedSnapshot,
): void {
  const u: UnsavedUpdates = { ...s.unsavedUpdates };
  const text = (s.manualText ?? '').trim();
  if (text) u.manual_context = { text };
  storage.setItem(draftKey(projectId), JSON.stringify(u));
}

/**
 * Load a persisted draft back into an `UnsavedUpdates` bag (legacy `loadDraft`).
 * Returns `null` when no draft exists or the stored JSON is corrupt (the legacy
 * code swallowed parse errors with a console.error and continued).
 */
export function loadLocalDraft(
  storage: Storage,
  projectId: number | string,
): UnsavedUpdates | null {
  const raw = storage.getItem(draftKey(projectId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<UnsavedUpdates>;
    return {
      client_name: parsed.client_name ?? null,
      role_name: parsed.role_name ?? null,
      contact: parsed.contact ?? null,
      manual_context: parsed.manual_context ?? null,
      ...(parsed.client_id !== undefined ? { client_id: parsed.client_id } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Discard a draft: remove it from storage and return a fresh bag (legacy
 * `clearDraft` which calls `localStorage.removeItem` + `resetUnsavedUpdates`).
 */
export function clearDraft(storage: Storage, projectId: number | string): UnsavedUpdates {
  storage.removeItem(draftKey(projectId));
  return resetUnsavedUpdates();
}

/** The choice a user makes in the unsaved-changes dialog. */
export type UnsavedDialogChoice = 'save' | 'discard' | 'cancel';

/**
 * Resolve a navigation attempt against the unsaved state (legacy `navigate` →
 * `showUnsavedDialog`). When there is nothing unsaved the navigation proceeds
 * immediately; otherwise the caller must open the dialog and feed the user's
 * choice to {@link applyDialogChoice}.
 */
export function resolveNavigation(s: UnsavedSnapshot): {
  readonly proceed: boolean;
  readonly needsDialog: boolean;
} {
  if (!hasUnsavedChanges(s)) return { proceed: true, needsDialog: false };
  return { proceed: false, needsDialog: true };
}

/** The action a Save/Discard/Cancel choice maps to (drives the React handler). */
export interface DialogOutcome {
  /** Persist the changes (call the save mutation) before navigating. */
  readonly save: boolean;
  /** Drop the draft (clear localStorage + reset bag) before navigating. */
  readonly discard: boolean;
  /** Continue with the queued navigation after save/discard. */
  readonly navigate: boolean;
}

/**
 * Map a dialog choice to an outcome (legacy `showUnsavedDialog` button handlers):
 *   - Save    → save, then navigate;
 *   - Discard → clearDraft, then navigate;
 *   - Cancel  → do nothing, stay (dialog just closes).
 */
export function applyDialogChoice(choice: UnsavedDialogChoice): DialogOutcome {
  switch (choice) {
    case 'save':
      return { save: true, discard: false, navigate: true };
    case 'discard':
      return { save: false, discard: true, navigate: true };
    case 'cancel':
    default:
      return { save: false, discard: false, navigate: false };
  }
}
